import useDiscard from "../../useDiscard";
import useWeakMap from "../../utils/useWeakMap";
import { ATTRIBUTE_FOR, ATTRIBUTE_FOR_REF, ATTRIBUTE_HTML, ATTRIBUTE_IF, ATTRIBUTE_PREFIX_BIND_ATTR, ATTRIBUTE_PREFIX_BIND_ATTR_SHORT, ATTRIBUTE_PREFIX_EVENT, ATTRIBUTE_REF, ATTRIBUTE_TWO_WAY } from "../enum";
import { extractAttributeFromNode, getAttributeName } from "../util";
import useEventBinding from "./event/eventBinding";
import useInterpolationForAttribute from "./interpolation/attr";
import useBindAttr from "./interpolation/bind-attr";
import useHTMLInterpolation from "./interpolation/html";
import useInterpolationForText from "./interpolation/text";
import useRefElement from "./ref/element";
import useRefElementList from "./ref/elementList";
import useFor, { checkNodeIsInLoopTemplate } from "./structure/for";
import useIf, { checkNodeIsVisible } from "./structure/if";
import useTwoWay from "./two-way/useTwoWay";


const canBindAttr = objAttr => Object.keys(objAttr).length > 0;

const clearAttributeTextOnUI = (/**
 * @returns {(node: Node) => void}
 */
    () => {
        const needRemoveAttrs = new Set([
            ATTRIBUTE_IF,
            ATTRIBUTE_FOR,
            ATTRIBUTE_REF,
            ATTRIBUTE_TWO_WAY,
            ATTRIBUTE_HTML
        ])

        const startWithByRcz = k => k.startsWith(ATTRIBUTE_PREFIX_EVENT) || k.startsWith(ATTRIBUTE_PREFIX_BIND_ATTR_SHORT) || k.startsWith(ATTRIBUTE_PREFIX_BIND_ATTR_SHORT)

        const canRemove = (k) => needRemoveAttrs.has(k) || startWithByRcz(k);

        return (node, attrs) => {
            for (let k in attrs) {
                if (canRemove(k)) {
                    node.removeAttribute(k);
                }
            }
        }
    })()

const groupAttribute = (() => {

    const isIf = name => name === ATTRIBUTE_IF
    const isFor = name => name === ATTRIBUTE_FOR
    const isEvent = name => name.startsWith(ATTRIBUTE_PREFIX_EVENT)
    const isRef = name => name === ATTRIBUTE_REF
    const isTwoWay = name => name === ATTRIBUTE_TWO_WAY
    const isHTML = name => name === ATTRIBUTE_HTML
    const isBindAttr = name => name.startsWith(ATTRIBUTE_PREFIX_BIND_ATTR) || name.startsWith(ATTRIBUTE_PREFIX_BIND_ATTR_SHORT)


    return (attr) => {
        const result = {
            ifAttr: {},
            forAttr: {},
            eventsAttr: {},
            othersAttr: {},
            refAttribute: {},
            twoWayAttr: {},
            htmlAttr: {},
            bindAttr: {},
        };
        for (let k in attr) {
            if (isIf(k)) {
                result.ifAttr[k] = attr[k];
            } else if (isFor(k)) {
                result.forAttr[k] = attr[k];
            } else if (isEvent(k)) {
                result.eventsAttr[k] = attr[k];
            } else if (isRef(k)) {
                result.refAttribute[k] = attr[k];
            } else if (isTwoWay(k))
                result.twoWayAttr[k] = attr[k];
            else if (isHTML(k)) {
                result.htmlAttr[k] = attr[k];
            } else if (isBindAttr(k)) {
                result.bindAttr[k] = attr[k];
            } else {
                result.othersAttr[k] = attr[k];
            }
        }
        return result;
    }
})();

const bindIf = async (props, node, attr) => {
    const condition = attr[ATTRIBUTE_IF];
    if (condition) {
        await useIf(condition, props, node);
    }
}

const bindFor = async (props, node, attr) => {
    const sentence = attr[ATTRIBUTE_FOR];
    if (sentence) {
        await useFor(sentence, props, node);
    }
}

const bindInterpolationForAttribute = async (props, node, attr) => {
    for (let k in attr) {
        await useInterpolationForAttribute({ attrName: k, attrValue: attr[k] }, props, node);
    }
}


const bindInterpolationForHTML = async (props, node, attr) => {
    for (let k in attr) {
        await useHTMLInterpolation({ attrName: k, attrValue: attr[k] }, props, node);
    }
}

const bindInterpolationForText = async (props, node) => {
    await useInterpolationForText(props, node);
}


const bindEvent = async (props, node, attr) => {
    for (let k in attr) {
        useEventBinding({ attrName: k, attrValue: attr[k] }, props, node);
    }
}

const bindRefElement = async (props, node, attr) => {
    const refName = attr[ATTRIBUTE_REF];
    if (refName) {
        await useRefElement(refName, props, node);
        await useRefElementList(refName, props, node);
    }
}

const bindRefListElement = async (props, node, attr) => {
    const refName = attr[ATTRIBUTE_REF];
    if (refName) {
        await useRefElement(refName, props, node);
        await useRefElementList(refName, props, node);
    }
}

const bindAttr = async (props, node, attr) => {
    if (!canBindAttr(attr)) {
        return;
    }
    for (let k in attr) {
        await useBindAttr({ attrName: k, attrValue: attr[k] }, props, node);
    }
}


const bindTwoWay = (props, node, attr) => {
    const name = attr[ATTRIBUTE_TWO_WAY];
    if (name) {
        useTwoWay(name, props, node);
    }
}

const useBinding = (() => {

    const attributeCaching = useWeakMap()

    const onDiscardProps = props => attributeCaching.delete(props);

    /**
     * @param {object} this.props
     * @param {Node} node
     */
    return async (props, node) => {

        if (checkNodeIsInLoopTemplate(node)) {
            // This Node is used in Loop template
            return Promise.resolve();
        }
        /**@type Object.<string,string> */
        const attr = extractAttributeFromNode(node);

        useDiscard(props, onDiscardProps);
        attributeCaching.set(node, attr);

        const { eventsAttr, forAttr, ifAttr, othersAttr, refAttribute, twoWayAttr, htmlAttr, bindAttr: attrForBind } = groupAttribute(attr);
        // Priority #1
        await bindIf(props, node, ifAttr);

        // Priority #2
        await bindFor(props, node, forAttr);

        await bindEvent(props, node, eventsAttr);

        await bindRefElement(props, node, refAttribute);

        // Other
        await bindInterpolationForAttribute(props, node, othersAttr);

        // // HTML & TEXT Binding
        await bindInterpolationForText(props, node);

        await bindInterpolationForHTML(props, node, htmlAttr);

        await bindAttr(props, node, attrForBind)

        bindTwoWay(props, node, twoWayAttr);

        clearAttributeTextOnUI(node, attr);
    }
})();

/**
 * 
 * @param {Node} node 
 */
export const canBindingNode = (node) => {
    return checkNodeIsVisible(node);
}

export default useBinding;